import { NavLink } from 'react-router-dom'
import { X } from 'lucide-react'
import { useMe } from '../hooks/useMe'
import { useBasePath } from '../hooks/useBasePath'
import { usePermission } from '../../hooks/usePermission'
import type { Permission } from '../../types'
import { SidebarUserMenu } from './SidebarUserMenu'

interface OnboardingSidebarProps {
  onClose?: () => void
}

const navItemPaths: { path: string; label: string; icon: React.ReactNode; permission?: Permission }[] = [
  {
    path: 'dashboard',
    label: 'Dashboard',
    permission: 'view_dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  // {
  //   path: 'users',
  //   label: 'Users',
  //   icon: (
  //     <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
  //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
  //     </svg>
  //   ),
  // },
  {
    path: 'orders',
    label: 'Orders',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
    ),
  },
  // Profile moved into the user-card ⋮ menu as "Account" (DEV-48214 §12.3).
  {
    path: 'settings',
    label: 'Settings',
    permission: 'view_settings',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  // Spend Caps sidebar entry removed 2026-07-15 per client request. The
  // route `settings/spend-caps` and `SpendCapsSettings` component still
  // exist so any bookmarked URL keeps working; only the top-level nav
  // affordance is gone.
]

export function OnboardingSidebar({ onClose }: OnboardingSidebarProps = {}) {
  const { data: meData } = useMe()
  const basePath = useBasePath()
  const { can } = usePermission()

  const navItems = navItemPaths
    .filter(item => !item.permission || can(item.permission))
    .map(item => ({
      ...item,
      to: `${basePath}/${item.path}`,
    }))

  // Close sidebar when clicking a link on mobile
  const handleNavClick = () => {
    if (onClose && window.innerWidth < 1024) {
      onClose()
    }
  }

  return (
    <div className="flex flex-col h-full bg-nav border-r border-nav-divider">
      {/* Logo + Org Name */}
      <div className="px-6 py-5 border-b border-nav-divider">
        <div className="flex items-center gap-3">
          {meData?.tenant?.logo_url ? (
            <img
              src={meData.tenant.logo_url}
              alt={meData.tenant.name}
              className="h-8 w-8 object-contain rounded flex-shrink-0"
            />
          ) : (
            <div className="h-8 w-8 rounded bg-brand-primary flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-[var(--brand-primary-text)]">
                {meData?.tenant?.name?.[0] || 'S'}
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-nav truncate">{meData?.tenant?.name || 'SupplyScanner'}</h1>
            <p className="text-xs text-nav-inactive">Admin panel</p>
          </div>
          {/* Close button for mobile */}
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden p-2 -mr-2 rounded-lg text-nav-inactive hover:text-nav hover:bg-nav-hover"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={handleNavClick}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-brand-primary text-[var(--brand-primary-text)]'
                  : 'text-nav hover:bg-nav-hover'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className={isActive ? 'text-[var(--brand-primary-text)]' : 'text-nav-inactive group-hover:text-nav'}>
                  {item.icon}
                </span>
                {item.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User card with ⋮ menu → Account + Sign out (DEV-48214 §12.3). */}
      <SidebarUserMenu
        firstName={meData?.first_name}
        lastName={meData?.last_name}
        email={meData?.email}
      />
    </div>
  )
}
